import React, { useMemo } from 'react';
import { CircleUser } from 'lucide-react';
import { ResumePreviewProps, WorkExperience } from '../types';
import { renderSocialLink } from '../utils/socialLinkRenderer';
import { S3_BASE_URL } from '../../../../config';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';

  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  
  if (yearOnly) {
    return date.getFullYear().toString();
  }
  
  if (!dateFormat) {
  const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  }

  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);

  switch (dateFormat) {
    case '%b %y':
      return `${monthShort} ${yearShort}`;
    case '%B %Y':
      return `${monthLong} ${year}`;
    case '%b %Y':
      return `${monthShort} ${year}`;
    case '%m/%Y':
      return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y':
      return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y':
      return year.toString();
    default:
      return `${monthShort} ${year}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string): string => {
  if (!startDate) return '';

  const start = formatDateWithFormat(startDate, dateFormat);
  if (isCurrent || !endDate) {
    return `${start} - Present`;
  }
  const end = formatDateWithFormat(endDate, dateFormat);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

interface EditableLineProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableLine: React.FC<EditableLineProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;

  return (
    <li
      className={`resume-editable-line ${isActive ? 'resume-editable-line--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </li>
  );
};

export const DefaultTemplate: React.FC<ResumePreviewProps> = ({ data, activeField, onFieldClick, sectionOrder, structures = [], settings }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    skills,
    categorySkills,
    customSections,
    links
  } = data;

  const getFontFamily = (fontType?: string) => {
    if (!fontType) return undefined;
    const fontMap: Record<string, string> = {
      'Arial': 'Arial, sans-serif',
      'Bitter': '"Bitter", serif',
      'BreeSerif': '"Bree Serif", serif',
      'Carlito': '"Carlito", sans-serif',
      'EB Garamond': '"EB Garamond", serif',
      'Inter': '"Inter", sans-serif',
      'Lora': '"Lora", serif',
      'Noto Serif': '"Noto Serif", serif',
      'Roboto': '"Roboto", sans-serif',
      'Source Sans': '"Source Sans Pro", sans-serif',
      'Neuton': '"Neuton", serif',
    };
    return fontMap[fontType] || undefined;
  };

  const getLineHeight = (lineHeight?: string) => {
    if (!lineHeight) return undefined;
    const lineHeightMap: Record<string, string> = {
      '1 Tight': '1',
      '1.15 Condensed': '1.15',
      '1.3 Standard': '1.3',
      '1.5 Relaxed': '1.5',
      '2 Spacious': '2',
    };
    return lineHeightMap[lineHeight] || undefined;
  };

  const previewStyle: React.CSSProperties = useMemo(() => ({
    fontSize: settings?.font_size ? `${settings.font_size}pt` : undefined,
    lineHeight: getLineHeight(settings?.line_height),
    fontFamily: getFontFamily(settings?.text_font),
  }), [settings?.font_size, settings?.line_height, settings?.text_font]);

  const headingFontFamily = useMemo(() => getFontFamily(settings?.heading_font), [settings?.heading_font]);
  const displayFontFamily = useMemo(() => getFontFamily(settings?.display_font), [settings?.display_font]);
  const dateFormat = settings?.date_format;

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) {
        map[sectionId] = s.structure_name.toUpperCase();
      }
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  const defaultOrder = ['about', 'experience', 'volunteer', 'education', 'certifications', 'projects', 'skills'];
  const order = sectionOrder || defaultOrder;

  const primaryLink = links?.find(l => l.type === 'portfolio')?.url || '';

  return (
    <div className="resume-preview resume-preview--default" style={previewStyle}>
      {settings?.is_headshot && (
        <div className="resume-default__headshot">
          {settings.headshot_url ? (
            <img
              src={settings.headshot_url.startsWith('http') ? settings.headshot_url : `${S3_BASE_URL}${settings.headshot_url}`}
              alt="Headshot"
              crossOrigin="anonymous"
              className={`resume-default__headshot-img ${settings.headshot_format === 'circle' ? 'resume-default__headshot-img--circle' : 'resume-default__headshot-img--square'}`}
              onError={(e) => {
                const url = settings.headshot_url;
                if (url) {
                  console.error('Failed to load headshot image:', url);
                  console.error('Full URL:', url.startsWith('http') ? url : `${S3_BASE_URL}${url}`);
                }
              }}
            />
          ) : (
            <div className={`resume-default__headshot-placeholder ${settings.headshot_format === 'circle' ? 'resume-default__headshot-placeholder--circle' : 'resume-default__headshot-placeholder--square'}`}>
              <CircleUser size={24} />
            </div>
          )}
        </div>
      )}
      <div className="resume-default__header">
        <div style={{ flex: 1 }}>
          <h1 className="resume-default__name" style={displayFontFamily ? { fontFamily: displayFontFamily } : undefined}>
          <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.firstName}
          </EditableField>
          {' '}
          <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.lastName}
          </EditableField>
          {targetJobTitle && (
            <>
              <span className="resume-default__position"> | </span>
              <EditableField fieldName="position" activeField={activeField} onFieldClick={onFieldClick} className="resume-default__position">
                {targetJobTitle}
              </EditableField>
            </>
          )}
        </h1>
        <p className="resume-default__contact">
          <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.email}
          </EditableField>
          {personalInfo.phone && (
            <>
              {' | '}
              <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.phone}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'linkedin') && (
            <>
              {' | '}
              <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('linkedin', links.find(l => l.type === 'linkedin')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'telegram') && (
            <>
              {' | '}
              <EditableField fieldName="telegram" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('telegram', links.find(l => l.type === 'telegram')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'whatsapp') && (
            <>
              {' | '}
              <EditableField fieldName="whatsapp" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('whatsapp', links.find(l => l.type === 'whatsapp')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'github') && (
            <>
              {' | '}
              <EditableField fieldName="github" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('github', links.find(l => l.type === 'github')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'twitter') && (
            <>
              {' | '}
              <EditableField fieldName="twitter" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('twitter', links.find(l => l.type === 'twitter')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {links?.find(l => l.type === 'dribbble') && (
            <>
              {' | '}
              <EditableField fieldName="dribbble" activeField={activeField} onFieldClick={onFieldClick}>
                {renderSocialLink('dribbble', links.find(l => l.type === 'dribbble')!.url, settings?.social_display_format, onFieldClick, activeField)}
              </EditableField>
            </>
          )}
          {primaryLink && (
            <>
              {' | '}
              <EditableField fieldName="link" activeField={activeField} onFieldClick={onFieldClick}>
                <a 
                  href={primaryLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onFieldClick?.('link');
                  }}
                >
                  Portfolio
                </a>
              </EditableField>
            </>
          )}
          {personalInfo.location && (
            <>
              {' | '}
              <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.location}
              </EditableField>
            </>
          )}
        </p>
        </div>
      </div>

      {order.map((sectionId) => {
        if (sectionId === 'about' && professionalSummary) {
          return (
            <div key="about" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('about', 'SUMMARY')}</h2>
              <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
                <ul className="resume-default__bullet-list">
                  {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                    <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
                  ))}
                </ul>
              </EditableBlock>
            </div>
          );
        }

        if (sectionId === 'experience' && workExperiences && workExperiences.length > 0) {
          return (
            <div key="experience" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('experience', 'PROFESSIONAL EXPERIENCE')}</h2>
              {workExperiences.map((experience: WorkExperience, expIndex: number) => (
            <EditableBlock
              key={experience.uid}
              fieldName={`experience.${experience.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-default__experience"
            >
              <div className="resume-default__experience-header">
                <div className="resume-default__experience-left">
                  <EditableField
                    fieldName={`experience.${experience.uid}.job_title`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-default__job-title"
                  >
                    {experience.job_title}
                  </EditableField>
                  <span className="resume-default__separator"> | </span>
                  <EditableField
                    fieldName={`experience.${experience.uid}.employer_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-default__company"
                  >
                    {experience.employer_name}
                    {experience.location && ` (${experience.location})`}
                  </EditableField>
                </div>
                {!experience.hideDate && (
                  <span className="resume-default__date">
                    <EditableField
                      fieldName={`experience.${experience.uid}.start_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {formatDateWithFormat(experience.start_date, dateFormat, experience.yearOnly)}
                    </EditableField>
                    {' - '}
                    <EditableField
                      fieldName={experience.is_current ? `experience.${experience.uid}.is_current` : `experience.${experience.uid}.end_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {experience.is_current ? 'Present' : formatDateWithFormat(experience.end_date, dateFormat, experience.yearOnly)}
                    </EditableField>
                  </span>
                )}
              </div>
              {experience.description && (
                <EditableField
                  fieldName={`experience.${experience.uid}.description`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-default__company-description"
                >
                  {experience.description}
                </EditableField>
              )}
              {experience.achievements && experience.achievements.length > 0 && (
                <ul className="resume-default__bullet-list">
                  {experience.achievements.map((achievement, achIndex) => (
                    <EditableLine
                      key={achIndex}
                      fieldName={`experience.${experience.uid}.achievement.${achIndex}`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {achievement}
                    </EditableLine>
                  ))}
                </ul>
              )}
            </EditableBlock>
          ))}
            </div>
          );
        }

        if (sectionId === 'volunteer' && volunteerExperiences && volunteerExperiences.length > 0) {
          return (
            <div key="volunteer" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('volunteer', 'VOLUNTEER EXPERIENCE')}</h2>
              {volunteerExperiences.map((experience: WorkExperience, expIndex: number) => (
                <EditableBlock
                  key={experience.uid}
                  fieldName={`experience.${experience.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-default__experience"
                >
                  <div className="resume-default__experience-header">
                    <div className="resume-default__experience-left">
                      <EditableField
                        fieldName={`experience.${experience.uid}.job_title`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__job-title"
                      >
                        {experience.job_title}
                      </EditableField>
                      <span className="resume-default__separator"> | </span>
                      <EditableField
                        fieldName={`experience.${experience.uid}.employer_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__company"
                      >
                        {experience.employer_name}
                        {experience.location && ` (${experience.location})`}
                      </EditableField>
                    </div>
                    {!experience.hideDate && (
                      <span className="resume-default__date">
                        <EditableField
                          fieldName={`experience.${experience.uid}.start_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {formatDateWithFormat(experience.start_date, dateFormat, experience.yearOnly)}
                        </EditableField>
                        {' - '}
                        <EditableField
                          fieldName={experience.is_current ? `experience.${experience.uid}.is_current` : `experience.${experience.uid}.end_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {experience.is_current ? 'Present' : formatDateWithFormat(experience.end_date, dateFormat, experience.yearOnly)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                  {experience.description && (
                    <EditableField
                      fieldName={`experience.${experience.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-default__company-description"
                    >
                      {experience.description}
                    </EditableField>
                  )}
                  {experience.achievements && experience.achievements.length > 0 && (
                    <ul className="resume-default__bullet-list">
                      {experience.achievements.map((achievement, achIndex) => (
                        <EditableLine
                          key={achIndex}
                          fieldName={`experience.${experience.uid}.achievement.${achIndex}`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {achievement}
                        </EditableLine>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        if (sectionId === 'education' && educations && educations.length > 0) {
          return (
            <div key="education" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('education', 'EDUCATION')}</h2>
              {educations.map((education) => (
                <EditableBlock
                  key={education.uid}
                  fieldName={`education.${education.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-default__experience"
                >
                  <div className="resume-default__experience-header">
                    <div className="resume-default__experience-left">
                      <EditableField
                        fieldName={`education.${education.uid}.degree`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__job-title"
                      >
                        {education.degree}
                      </EditableField>
                      {education.field_of_study && (
                        <>
                          <span className="resume-default__separator"> in </span>
                          <EditableField
                            fieldName={`education.${education.uid}.field_of_study`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                            className="resume-default__company"
                          >
                            {education.field_of_study}
                          </EditableField>
                        </>
                      )}
                      <span className="resume-default__separator"> | </span>
                      <EditableField
                        fieldName={`education.${education.uid}.institution`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__company"
                      >
                        {education.institution}
                      </EditableField>
                    </div>
                    {education.graduation_date && (
                      <span className="resume-default__date">
                        <EditableField
                          fieldName={`education.${education.uid}.graduation_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {formatDateWithFormat(education.graduation_date, dateFormat)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                  {education.grade && (
                    <p className="resume-default__company-description">
                      <EditableField
                        fieldName={`education.${education.uid}.grade`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        GPA: {education.grade}
                      </EditableField>
                    </p>
                  )}
                  {education.description && (
                    <EditableField
                      fieldName={`education.${education.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-default__company-description"
                    >
                      {education.description}
                    </EditableField>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        if (sectionId === 'certifications' && certifications && certifications.length > 0) {
          return (
            <div key="certifications" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('certifications', 'CERTIFICATIONS')}</h2>
              {certifications.map((cert) => {
                const isStacked = settings?.layout === 'Stacked';
                return (
                <EditableBlock
                  key={cert.uid}
                  fieldName={`certification.${cert.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-default__experience"
                >
                  <div className="resume-default__experience-header">
                    <div className="resume-default__experience-left">
                      <EditableField
                        fieldName={`certification.${cert.uid}.certification_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__job-title"
                      >
                        {cert.certification_name}
                      </EditableField>
                        {!isStacked && cert.organization_name && (
                        <>
                          <span className="resume-default__separator"> | </span>
                          <EditableField
                            fieldName={`certification.${cert.uid}.organization_name`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                            className="resume-default__company"
                          >
                            {cert.organization_name}
                          </EditableField>
                        </>
                      )}
                        {!isStacked && cert.credential_id && (
                          <>
                            <span className="resume-default__separator"> | </span>
                            <EditableField
                              fieldName={`certification.${cert.uid}.credential_id`}
                              activeField={activeField}
                              onFieldClick={onFieldClick}
                              className="resume-default__company"
                            >
                              {cert.credential_id}
                            </EditableField>
                          </>
                        )}
                    </div>
                    {cert.issued_at && (
                      <span className="resume-default__date">
                        <EditableField
                          fieldName={`certification.${cert.uid}.issued_at`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                            {formatDateWithFormat(cert.issued_at, dateFormat)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                    {isStacked && (cert.organization_name || cert.credential_id) && (
                      <p className="resume-default__company-description">
                        {cert.organization_name && (
                          <EditableField
                            fieldName={`certification.${cert.uid}.organization_name`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                          >
                            {cert.organization_name}
                          </EditableField>
                        )}
                        {cert.organization_name && cert.credential_id && ' | '}
                  {cert.credential_id && (
                      <EditableField
                        fieldName={`certification.${cert.uid}.credential_id`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                            {cert.credential_id}
                      </EditableField>
                        )}
                    </p>
                  )}
                  {cert.description && (
                    <EditableField
                      fieldName={`certification.${cert.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-default__company-description"
                    >
                      {cert.description}
                    </EditableField>
                  )}
                </EditableBlock>
                );
              })}
            </div>
          );
        }

        if (sectionId === 'projects' && projects && projects.length > 0) {
          return (
            <div key="projects" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('projects', 'PROJECTS')}</h2>
              {projects.map((project) => (
                <EditableBlock
                  key={project.uid}
                  fieldName={`project.${project.uid}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                  className="resume-default__experience"
                >
                  <div className="resume-default__experience-header">
                    <div className="resume-default__experience-left">
                      <EditableField
                        fieldName={`project.${project.uid}.project_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                        className="resume-default__job-title"
                      >
                        {project.project_name}
                      </EditableField>
                      {project.url && (
                        <>
                          <span className="resume-default__separator"> | </span>
                          <EditableField
                            fieldName={`project.${project.uid}.url`}
                            activeField={activeField}
                            onFieldClick={onFieldClick}
                            className="resume-default__link"
                          >
                            {project.url}
                          </EditableField>
                        </>
                      )}
                    </div>
                    {project.start_date && (
                      <span className="resume-default__date">
                        <EditableField
                          fieldName={`project.${project.uid}.start_date`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {formatDateWithFormat(project.start_date, dateFormat)}
                        </EditableField>
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <EditableField
                      fieldName={`project.${project.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-default__company-description"
                    >
                      {project.description}
                    </EditableField>
                  )}
                  {project.achievements && project.achievements.length > 0 && (
                    <ul className="resume-default__bullet-list">
                      {project.achievements.map((achievement, achIndex) => (
                        <EditableLine
                          key={achIndex}
                          fieldName={`project.${project.uid}.achievement.${achIndex}`}
                          activeField={activeField}
                          onFieldClick={onFieldClick}
                        >
                          {achievement}
                        </EditableLine>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          );
        }

        const hasSkillsInCategories = categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0);
        if (sectionId === 'skills' && ((skills && skills.length > 0) || hasSkillsInCategories)) {
          const categoriesWithSkills = categorySkills ? Object.entries(categorySkills).filter(([_, skillsList]) => skillsList && skillsList.length > 0) : [];
          
          return (
            <div key="skills" className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{getSectionTitle('skills', 'SKILLS')}</h2>
              <ul className="resume-default__bullet-list">
                {categoriesWithSkills.map(([category, categorySkillsList]) => (
                  <li key={category}>
                    <EditableField
                      fieldName={`skills.category.${category}`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      <span className="resume-default__skill-category">{category}</span>: {categorySkillsList.join(', ')}.
                    </EditableField>
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        const customSection = customSections?.find(cs => cs.id === sectionId);
        if (customSection) {
          return (
            <div key={customSection.id} className="resume-default__section">
              <h2 className="resume-default__section-title" style={headingFontFamily ? { fontFamily: headingFontFamily } : undefined}>{customSection.title.toUpperCase()}</h2>
              {customSection.layout === 'stacked' ? (
                customSection.entries.map((entry) => (
                  <div key={entry.id} className="resume-default__experience">
                    <div className="resume-default__experience-header">
                      <div className="resume-default__experience-left">
                        <span className="resume-default__job-title">{entry.title}</span>
                      </div>
                      {entry.date && !entry.hideDate && (
                        <span className="resume-default__date">
                          {entry.yearOnly
                            ? new Date(entry.date).getFullYear()
                            : formatDateWithFormat(entry.date, dateFormat)}
                          {entry.endDate && !entry.hideEndDate && (
                            <> - {entry.yearOnly
                              ? new Date(entry.endDate).getFullYear()
                              : formatDateWithFormat(entry.endDate, dateFormat)}</>
                          )}
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="resume-default__company-description">{entry.description}</p>
                    )}
                  </div>
                ))
              ) : (
                <ul className="resume-default__bullet-list">
                  {customSection.entries.map((entry) => (
                    <li key={entry.id}>
                      <strong>{entry.title}</strong>
                      {entry.description && `: ${entry.description}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};
